var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { PAGINATION_PAGE_SIZE } from '@web/config';
import { mockData as users } from '@web/lib/demo/data/users';
import { diffComparisonType, diffComparisonValueFormat } from '@workalizer/shared/types';
import { calculateAverage, createPeriodComparisonMatcherFromItems, getPercentageDiff, randomInt, toPeriodComparisonMatchOptions } from '@workalizer/shared/utils';
import { eachDayOfInterval, format } from 'date-fns';
export var createDemoPeriodComparisonMatcher = function (input, interval, currentPeriods, previousPeriods) {
    return createPeriodComparisonMatcherFromItems(currentPeriods, previousPeriods, toPeriodComparisonMatchOptions(input.dateFrom, input.dateTo, interval));
};
export var DEMO_SUMMARY_TEAM_MEMBER_COUNT = 4;
export var buildDemoSummaryTeamAverageDiff = function (count, teamMemberCount) {
    if (teamMemberCount === void 0) { teamMemberCount = DEMO_SUMMARY_TEAM_MEMBER_COUNT; }
    var teamTotal = randomInt(Math.round(count * teamMemberCount * 0.75), Math.round(count * teamMemberCount * 1.25));
    return getPercentageDiff(calculateAverage(teamTotal, teamMemberCount), count, { comparisonType: diffComparisonType.teamAverage });
};
export var buildDemoSummaryTeamAverageSecondsDiff = function (seconds, teamMemberCount) {
    if (teamMemberCount === void 0) { teamMemberCount = DEMO_SUMMARY_TEAM_MEMBER_COUNT; }
    var teamTotal = randomInt(Math.round(seconds * teamMemberCount * 0.75), Math.round(seconds * teamMemberCount * 1.25));
    return getPercentageDiff(calculateAverage(teamTotal, teamMemberCount), seconds, {
        comparisonValueFormat: diffComparisonValueFormat.durationSeconds,
        comparisonType: diffComparisonType.teamAverage
    });
};
export var filterData = function (data, filters) {
    if (!filters.length) {
        return data;
    }
    return data.filter(function (item) {
        return filters.every(function (filter) {
            if (filter.isSkip) {
                return true;
            }
            var itemValue = item[filter.key];
            var filterValue = filter.value;
            if (Array.isArray(filterValue)) {
                return filterValue.includes(itemValue);
            }
            switch (filter.operator) {
                case 'eq':
                    return itemValue === filterValue;
                case 'gt':
                    if (typeof itemValue === 'number' &&
                        typeof filterValue === 'number') {
                        return itemValue > filterValue;
                    }
                    return (new Date(itemValue) > new Date(filterValue));
                case 'lt':
                    if (typeof itemValue === 'number' &&
                        typeof filterValue === 'number') {
                        return itemValue < filterValue;
                    }
                    return (new Date(itemValue) < new Date(filterValue));
                case 'contains':
                    return itemValue.toString().includes(filterValue.toString());
                default:
                    return true;
            }
        });
    });
};
export var dateToTimestamp = function (date) {
    return new Date(date).getTime();
};
export var demoDataPagination = function (arr, options) {
    var _a, _b;
    var limit = (_a = options === null || options === void 0 ? void 0 : options.limit) !== null && _a !== void 0 ? _a : PAGINATION_PAGE_SIZE;
    var offset = (_b = options === null || options === void 0 ? void 0 : options.offset) !== null && _b !== void 0 ? _b : 0;
    return {
        data: arr.slice(offset, offset + limit),
        total: arr.length
    };
};
export var pickRandom = function (arr, count) {
    var shuffled = __spreadArray([], arr, true).sort(function () { return Math.random() - 0.5; });
    return shuffled.slice(0, Math.min(count, arr.length));
};
export var getUsers = function (_a) {
    var _b = _a === void 0 ? {} : _a, userId = _b.userId, orgUnitId = _b.orgUnitId;
    return users.filter(function (user) {
        var _a;
        if (userId) {
            return user.id === userId;
        }
        if (orgUnitId) {
            return (_a = user.userOrgUnits) === null || _a === void 0 ? void 0 : _a.some(function (unit) { return unit.orgUnit.id === orgUnitId; });
        }
        return true;
    });
};
export var getOrgUnits = function (_a) {
    var _b = _a === void 0 ? {} : _a, orgUnitId = _b.orgUnitId;
    var orgUnits = users
        .reduce(function (acc, user) {
        var _a;
        if (!((_a = user.userOrgUnits) === null || _a === void 0 ? void 0 : _a.length)) {
            return acc;
        }
        user.userOrgUnits.forEach(function (userOrgUnit) {
            if (!acc.some(function (orgUnit) { return orgUnit.id === userOrgUnit.orgUnit.id; })) {
                acc.push(__assign(__assign({}, userOrgUnit.orgUnit), { type: userOrgUnit.orgUnit.type, path: userOrgUnit.orgUnit.path || '', parentPath: userOrgUnit.orgUnit.parentPath || '' }));
            }
        });
        return acc;
    }, [])
        .filter(function (orgUnit) {
        if (orgUnitId) {
            return orgUnit.id === orgUnitId;
        }
        return true;
    });
    return orgUnits;
};
export var getUserOrgUnits = function () {
    return users.reduce(function (acc, user) {
        var _a;
        if (!((_a = user.userOrgUnits) === null || _a === void 0 ? void 0 : _a.length)) {
            return acc;
        }
        user.userOrgUnits.forEach(function (userOrgUnit) {
            if (!acc.some(function (orgUnit) { return orgUnit.id === userOrgUnit.orgUnit.id; })) {
                acc.push(__assign(__assign({}, userOrgUnit), { createdAt: new Date(userOrgUnit.createdAt), updatedAt: new Date(userOrgUnit.updatedAt) }));
            }
        });
        return acc;
    }, []);
};
export var generateRandomDailyData = function (dateFrom, dateTo) {
    var days = eachDayOfInterval({
        start: dateFrom,
        end: dateTo
    });
    var result = days.map(function (day) { return ({
        date: format(day, 'yyyy-MM-dd'),
        count: Math.floor(Math.random() * 11 + 1)
    }); });
    return result;
};
